import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useOrganization } from './OrganizationContext';
import { logger } from '../utils/logger';

export interface MetarData {
  raw: string;
  station: string;
  time: string;
  flight_category?: string;
  temperature?: number;
  temperature_f?: number;
  dewpoint?: number;
  dewpoint_f?: number;
  wind_speed?: number;
  wind_direction?: number;
  wind_gust?: number;
  visibility?: number;
  visibility_miles?: number;
  pressure?: number;
  pressure_inhg?: number;
  weather?: string[];
  clouds?: string[];
}

interface MetarContextType {
  metarData: MetarData | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const MetarContext = createContext<MetarContextType | undefined>(undefined);

export const MetarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { organization, loading: orgLoading } = useOrganization();
  const icaoCode = organization?.baseAirport?.icaoCode;
  const [metarData, setMetarData] = useState<MetarData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMetar = useCallback(async (icao: string) => {
    const sanitizedIcao = icao.trim().toUpperCase();

    if (!/^[A-Z]{3,4}$/.test(sanitizedIcao)) {
      logger.error('[MetarContext] Invalid ICAO code provided:', icao);
      setError('Invalid ICAO code configured for organization');
      setMetarData(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const apiKey = process.env.REACT_APP_CHECKWX_API_KEY;
      if (!apiKey) throw new Error('CheckWX API key not configured');
      const response = await fetch(`https://api.checkwx.com/metar/${sanitizedIcao}/decoded`, {
        headers: {
          'X-API-Key': apiKey,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        logger.error('[MetarContext] METAR fetch failed', {
          icao: sanitizedIcao,
          status: response.status,
          statusText: response.statusText
        });
        throw new Error(`Failed to fetch METAR: ${response.status}`);
      }
      const data = await response.json();
      if (data.data && data.data.length > 0) {
        const metar = data.data[0];
        setMetarData({
          raw: metar.raw_text,
          station: metar.icao,
          time: metar.observed,
          flight_category: metar.flight_category,
          temperature: metar.temperature?.celsius,
          temperature_f: metar.temperature?.fahrenheit,
          dewpoint: metar.dewpoint?.celsius,
          dewpoint_f: metar.dewpoint?.fahrenheit,
          wind_speed: metar.wind?.speed_kts,
          wind_direction: metar.wind?.degrees,
          wind_gust: metar.wind?.gust_kts,
          visibility: metar.visibility?.meters,
          visibility_miles: metar.visibility?.miles,
          pressure: metar.barometer?.hpa,
          pressure_inhg: metar.barometer?.hg,
          weather: metar.wx || [],
          clouds: metar.clouds ? metar.clouds.map((c: any) => c.text) : [],
        });
      } else {
        logger.warn('[MetarContext] No METAR data returned', {
          icao: sanitizedIcao,
          payload: data
        });
        throw new Error('No METAR data available');
      }
    } catch (err) {
      logger.error('[MetarContext] Error fetching METAR', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch METAR');
      setMetarData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    if (icaoCode) fetchMetar(icaoCode);
  }, [icaoCode, fetchMetar]);

  useEffect(() => {
    if (icaoCode) fetchMetar(icaoCode);
  }, [icaoCode, fetchMetar]);

  return (
    <MetarContext.Provider value={{ metarData, loading: loading || orgLoading, error, refresh }}>
      {children}
    </MetarContext.Provider>
  );
};

export function useMetarContext() {
  const ctx = useContext(MetarContext);
  if (!ctx) throw new Error('useMetarContext must be used within a MetarProvider');
  return ctx;
} 